// src/pages/admin/Contractors.jsx
import React, { useEffect, useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { getContractors, createContractor, updateContractor, getSettings, defaultSettings, tierPlainText, tierProgressForMember, nextAnnualResetDate } from "../../lib/db";
import { Btn, Badge, TableCard, Table, Modal, Field, Input, Select, Avatar, Toast, Spinner, fmt$, fmtDate, coercePlainText, ProgressBar } from "../../components/UI";

function commissionRateForTier(tierName, programSettings) {
  const tiers = programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers;
  const row = tiers.find(t => t.name === tierName);
  if (row?.rate != null) return row.rate;
  const fb = defaultSettings.tiers.find(t => t.name === tierName);
  return fb?.rate ?? defaultSettings.tiers[0].rate;
}

function inferCommissionOverride(designer, programSettings) {
  if (designer.commissionPending === true) return false;
  const tierName = tierPlainText(designer.tier);
  const tierRate = commissionRateForTier(tierName, programSettings);
  const profileRate = Number(designer.commissionRate);
  if (!Number.isFinite(profileRate)) return false;
  return Math.abs(profileRate - tierRate) > 0.001;
}

function emptyAddForm(programSettings) {
  const tiers = programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers;
  const tier = tiers[0].name;
  return {
    firstName: "", lastName: "", firm: "", email: "", phone: "",
    tier,
    commissionRate: commissionRateForTier(tier, programSettings),
    commissionOverride: false,
    tempPass: "",
  };
}

export default function Contractors() {
  const [contractors, setContractors] = useState([]);
  const [programSettings, setProgramSettings] = useState(defaultSettings);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [toast, setToast]         = useState("");
  const [search, setSearch]       = useState("");

  const [form, setForm] = useState(() => emptyAddForm(defaultSettings));
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = async () => {
    setLoading(true);
    const [list, settings] = await Promise.all([getContractors(), getSettings()]);
    setContractors(list);
    setProgramSettings(settings);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.tempPass || "ChangeMe123!");
      const tierName = coercePlainText(form.tier) || (programSettings?.tiers?.[0]?.name ?? "Partner");
      const parsed = Number(String(form.commissionRate).replace(/,/g, ""));
      const commissionRate = form.commissionOverride && Number.isFinite(parsed)
        ? parsed
        : commissionRateForTier(tierName, programSettings);
      await createContractor(cred.user.uid, {
        name: `${form.firstName} ${form.lastName}`,
        email: form.email, firm: form.firm, phone: form.phone,
        tier: tierName,
        commissionRate,
        commissionPending: false,
      });
      setModal(false);
      setForm(emptyAddForm(programSettings));
      toast && setToast("");
      setToast("Contractor added successfully!");
      setTimeout(() => setToast(""), 3000);
      load();
    } catch (err) { setToast(err.message); }
  };

  const handleEdit = async () => {
    const tierName = coercePlainText(editModal.tier) || "Partner";
    const parsed = Number(String(editModal.commissionRate).replace(/,/g, ""));
    const commissionRate = editModal.commissionOverride && Number.isFinite(parsed)
      ? parsed
      : commissionRateForTier(tierName, programSettings);
    const fulfill = {};
    if (editModal.fulfillDinner) {
      fulfill.prizePartnerPreferredPending = false;
      fulfill.prizePartnerPreferredFulfilled = true;
    }
    if (editModal.fulfillBag) {
      fulfill.prizePreferredPrincipalPending = false;
      fulfill.prizePreferredPrincipalFulfilled = true;
    }
    if (editModal.fulfillParis) {
      fulfill.prizeHalfMillionPending = false;
      fulfill.prizeHalfMillionFulfilled = true;
    }
    await updateContractor(editModal.id, {
      name: editModal.name, firm: editModal.firm,
      tier: tierName,
      commissionRate,
      status: editModal.status,
      commissionPending: false,
      ...fulfill,
    });
    setEditModal(null);
    setToast("Contractor updated."); setTimeout(() => setToast(""), 3000);
    load();
  };

  const filtered = contractors.filter(d =>
    !search || `${d.name} ${d.firm} ${d.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const cols = [
    { key: "dName", label: "Contractor" },
    { key: "tier",  label: "Tier" },
    { key: "tierBar", label: "Next tier" },
    { key: "sales", label: "YTD Sales" },
    { key: "rate",  label: "Rate" },
    { key: "earned",label: "Earned" },
    { key: "pending",label: "Pending Payout" },
    { key: "reset", label: "YTD resets" },
    { key: "prizes", label: "Prizes" },
    { key: "statusBadge", label: "Status" },
    { key: "actions", label: "" },
  ];

  const openEdit = (d) => setEditModal({
    ...d,
    commissionOverride: inferCommissionOverride(d, programSettings),
    fulfillDinner: false,
    fulfillBag: false,
    fulfillParis: false,
  });

  const rows = filtered.map(d => ({
    dName: (
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
        title="Adjust commission, tier, or status"
        onClick={() => openEdit(d)}
      >
        <Avatar name={d.name} />
        <div><div style={{ fontWeight: 400 }}>{d.name}</div><div style={{ fontSize: 11, color: "var(--mid)" }}>{d.firm}</div></div>
      </div>
    ),
    tier: <Badge label={tierPlainText(d.tier)} />,
    tierBar: (() => {
      const tp = tierProgressForMember(d, programSettings);
      return (
        <div style={{ minWidth: 130 }}>
          <ProgressBar value={tp.progressPct} max={100} label={tp.nextName ? `${tp.nextName} (${tp.nextRate}%)` : "Top tier"} />
        </div>
      );
    })(),
    sales: fmt$(d.ytdSales),
    rate: d.commissionPending === true ? <span style={{ fontSize: 12, color: "var(--gold)", fontWeight: 500 }}>Pending</span> : `${d.commissionRate ?? "—"}%`,
    earned: fmt$(d.ytdEarned),
    pending: fmt$(d.pendingPayout),
    reset: fmtDate(nextAnnualResetDate(d)),
    prizes: (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 140 }}>
        {d.prizePartnerPreferredPending ? <Badge label="Dinner" /> : null}
        {d.prizePreferredPrincipalPending ? <Badge label="LV bag" /> : null}
        {d.prizeHalfMillionPending ? <Badge label="Paris trip" /> : null}
        {!d.prizePartnerPreferredPending && !d.prizePreferredPrincipalPending && !d.prizeHalfMillionPending ? <span style={{ fontSize: 11, color: "var(--mid)" }}>—</span> : null}
      </div>
    ),
    statusBadge: <Badge label={d.status || "active"} />,
    actions: <Btn size="sm" onClick={() => openEdit(d)}>Edit</Btn>,
  }));

  return (
    <div>
      <Toast msg={toast} onClose={() => setToast("")} />
      <div style={{ padding: "36px 48px 0" }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 36, fontWeight: 300 }}>Contractors</h1>
        <p style={{ fontSize: 13, color: "var(--mid)", marginTop: 4 }}>Manage contractors and commission rates. Self-registered contractors show Pending until you save their profile.</p>
      </div>

      <div style={{ padding: "24px 48px 48px" }}>
        <TableCard
          title={`All Contractors (${filtered.length})`}
          actions={<>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{ padding:"8px 12px", border:"1px solid var(--border)", borderRadius:2, fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:300, background:"var(--cream)", outline:"none", width:200 }} />
            <Btn variant="gold" size="sm" onClick={() => { setForm(emptyAddForm(programSettings)); setModal(true); }}>+ Add Contractor</Btn>
          </>}
        >
          {loading ? <Spinner /> : <Table cols={cols} rows={rows} emptyMsg="No contractors yet." />}
        </TableCard>
      </div>

      {/* Add Designer Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title="Add Contractor"
        footer={<><Btn onClick={() => setModal(false)}>Cancel</Btn><Btn variant="gold" onClick={handleAdd}>Add Contractor</Btn></>}>
        <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
          <Field label="First Name" half><Input value={form.firstName} onChange={set("firstName")} /></Field>
          <Field label="Last Name" half><Input value={form.lastName} onChange={set("lastName")} /></Field>
        </div>
        <Field label="Email"><Input type="email" value={form.email} onChange={set("email")} /></Field>
        <Field label="Firm / Studio"><Input value={form.firm} onChange={set("firm")} /></Field>
        <Field label="Tier">
          <Select value={form.tier} onChange={e => {
            const t = e.target.value;
            setForm(f => ({
              ...f,
              tier: t,
              ...(!f.commissionOverride ? { commissionRate: commissionRateForTier(t, programSettings) } : {}),
            }));
          }}>
            {(programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers).map(t => (
              <option key={t.name} value={t.name}>{t.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Commission %" hint={form.commissionOverride ? undefined : "From program tier for this tier name. Enable override to set a custom rate."}>
          <Input
            type="text"
            inputMode="decimal"
            value={form.commissionRate}
            onChange={set("commissionRate")}
            disabled={!form.commissionOverride}
          />
        </Field>
        <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer", fontSize: 13, fontWeight: 300, color: "var(--charcoal)" }}>
          <input
            type="checkbox"
            checked={form.commissionOverride}
            onChange={e => {
              const on = e.target.checked;
              setForm(f => ({
                ...f,
                commissionOverride: on,
                ...(!on ? { commissionRate: commissionRateForTier(f.tier, programSettings) } : {}),
              }));
            }}
          />
          Override commission rate
        </label>
        <Field label="Temp Password (they'll be emailed to reset)"><Input type="password" value={form.tempPass} onChange={set("tempPass")} placeholder="ChangeMe123!" /></Field>
      </Modal>

      {/* Edit Modal */}
      {editModal && (
        <Modal open={!!editModal} onClose={() => setEditModal(null)} title={`Edit · ${editModal.name}`}
          footer={<><Btn onClick={() => setEditModal(null)}>Cancel</Btn><Btn variant="gold" onClick={handleEdit}>{editModal.commissionPending ? "Save & approve commission" : "Save"}</Btn></>}>
          <Field label="Name"><Input value={editModal.name} onChange={e=>setEditModal(m=>({...m,name:e.target.value}))} /></Field>
          <Field label="Firm"><Input value={editModal.firm} onChange={e=>setEditModal(m=>({...m,firm:e.target.value}))} /></Field>
          {editModal.commissionPending === true && (
            <div style={{ fontSize: 12, color: "var(--mid)", background: "var(--gold-pale)", border: "1px solid var(--border)", padding: "10px 12px", borderRadius: 2, marginBottom: 12 }}>
              <strong>Approve commission:</strong> Set tier (and optional override), then save. That clears “pending” and publishes their rate on the portal.
            </div>
          )}
          <Field label="Tier">
            <Select value={tierPlainText(editModal.tier)} onChange={e => {
              const t = e.target.value;
              setEditModal(m => ({
                ...m,
                tier: t,
                ...(!m.commissionOverride ? { commissionRate: commissionRateForTier(t, programSettings) } : {}),
              }));
            }}>
              {(programSettings?.tiers?.length ? programSettings.tiers : defaultSettings.tiers).map(t => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Commission %" hint={editModal.commissionOverride ? undefined : "From program tier; enable override to edit."}>
            <Input
              type="text"
              inputMode="decimal"
              value={editModal.commissionRate}
              onChange={e => setEditModal(m => ({ ...m, commissionRate: e.target.value }))}
              disabled={!editModal.commissionOverride}
            />
          </Field>
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, cursor: "pointer", fontSize: 13, fontWeight: 300, color: "var(--charcoal)" }}>
            <input
              type="checkbox"
              checked={!!editModal.commissionOverride}
              onChange={e => {
                const on = e.target.checked;
                setEditModal(m => ({
                  ...m,
                  commissionOverride: on,
                  ...(!on ? { commissionRate: commissionRateForTier(coercePlainText(m.tier) || "Partner", programSettings) } : {}),
                }));
              }}
            />
            Override commission rate
          </label>
          {(editModal.prizePartnerPreferredPending || editModal.prizePreferredPrincipalPending || editModal.prizeHalfMillionPending) && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 2, padding: "14px 16px", marginBottom: 16, background: "#fff" }}>
              <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--mid)", marginBottom: 12 }}>Milestone prizes</div>
              {editModal.prizePartnerPreferredPending && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!editModal.fulfillDinner} onChange={(e) => setEditModal((m) => ({ ...m, fulfillDinner: e.target.checked }))} />
                  <span>Tier 1 → Tier 2: Dinner on us — check when fulfilled</span>
                </label>
              )}
              {editModal.prizePreferredPrincipalPending && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!editModal.fulfillBag} onChange={(e) => setEditModal((m) => ({ ...m, fulfillBag: e.target.checked }))} />
                  <span>Tier 2 → Tier 3: Louis Vuitton bag — check when fulfilled</span>
                </label>
              )}
              {editModal.prizeHalfMillionPending && (
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 4, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!editModal.fulfillParis} onChange={(e) => setEditModal((m) => ({ ...m, fulfillParis: e.target.checked }))} />
                  <span>Tier 3 · $500k program-YTD: Trip (Paris / Italy) — check when fulfilled</span>
                </label>
              )}
            </div>
          )}
          <Field label="Status">
            <Select value={editModal.status} onChange={e=>setEditModal(m=>({...m,status:e.target.value}))}>
              <option value="active">Active</option><option value="inactive">Inactive</option>
            </Select>
          </Field>
        </Modal>
      )}
    </div>
  );
}
